import { useState, useCallback } from "react";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCaptcha = () => {
  const [captcha, setCaptcha] = useState(null);
  const [answer, setAnswer] = useState("");

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/captcha`);
    setCaptcha(res.data);
    setAnswer("");
  }, []);

  const checkRequired = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/captcha-required`);
      if (res.data.required) await load();
    } catch {
      /* fail open — server enforces */
    }
  }, [load]);

  const handleReject = useCallback(async (err) => {
    if (err.response?.status === 428) {
      await load();
      return "required";
    }
    const detail = err.response?.data?.detail;
    if (err.response?.status === 400 && typeof detail === "string" && detail.includes("human check")) {
      await load();
      return "wrong";
    }
    return null;
  }, [load]);

  const fields = captcha ? { captcha_token: captcha.token, captcha_answer: answer } : {};
  return { captcha, answer, setAnswer, load, checkRequired, handleReject, fields };
};

export const HumanCheck = ({ captcha, answer, setAnswer, onRefresh }) => {
  if (!captcha) return null;
  return (
    <div className="mt-6 border border-vortex-cyan/30 bg-vortex-cyan/5 p-5" data-testid="human-check">
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor="captcha-answer" className="font-mono text-[10px] tracking-[0.25em] text-vortex-cyan uppercase">
          Human check — {captcha.question}
        </Label>
        <button
          type="button"
          onClick={onRefresh}
          data-testid="human-check-refresh"
          aria-label="New question"
          className="shrink-0 text-slate-500 transition-colors hover:text-vortex-cyan"
        >
          <RefreshCw size={14} />
        </button>
      </div>
      <Input
        id="captcha-answer"
        data-testid="human-check-input"
        required
        inputMode="numeric"
        autoComplete="off"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        className="mt-3 border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan"
        placeholder="Your answer"
      />
    </div>
  );
};
